/**
 * RecordatorioRepository - Repositorio unificado para Recordatorios
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import { IRecordatorioRepository, Recordatorio, FiltroRecordatorios } from '../interfaces/IRecordatorioRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Recordatorios
 */
export class RecordatorioRepository extends BaseRepository<Recordatorio, string> implements IRecordatorioRepository {
  private profileRepo: IProfileRepository | null = null;

  /** Clause reutilizable para excluir soft-deletes (is_deleted=false). */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'recordatorios', 'recordatorios');

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.profileRepo = profileRepo;
      this.setProfileRepository(profileRepo);
    }
  }

  async add(data: Omit<Recordatorio, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Recordatorio = {
      ...data,
      id,
      user_id: data.user_id || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false
    };

    await this.db.insert('recordatorios', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<Recordatorio> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('recordatorios', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('recordatorios', id);
    this.emitSync('DELETE', id);
  }

  async getByFiltros(filtros: FiltroRecordatorios): Promise<Recordatorio[]> {
    const clauses: WhereClause[] = [this.NOT_DELETED];

    if (filtros.fecha_inicio) {
      clauses.push({
        field: 'date',
        operator: 'greaterThanOrEqual',
        value: filtros.fecha_inicio
      });
    }

    if (filtros.fecha_fin) {
      clauses.push({
        field: 'date',
        operator: 'lessThanOrEqual',
        value: filtros.fecha_fin
      });
    }

    if (filtros.priority) {
      clauses.push({
        field: 'priority',
        operator: 'equals',
        value: filtros.priority
      });
    }

    if (filtros.user_id) {
      clauses.push({
        field: 'user_id',
        operator: 'equals',
        value: filtros.user_id
      });
    }

    if (filtros.empresa_id) {
      clauses.push({
        field: 'empresa_id',
        operator: 'equals',
        value: filtros.empresa_id
      });
    }

    if (filtros.completed !== undefined) {
      clauses.push({
        field: 'completed',
        operator: 'equals',
        value: filtros.completed
      });
    }

    return this.db.queryMultiple<Recordatorio>('recordatorios', clauses, {
      column: 'date',
      direction: 'asc'
    });
  }

  async getByFecha(fecha: string): Promise<Recordatorio[]> {
    return this.db.queryMultiple<Recordatorio>('recordatorios', [
      this.NOT_DELETED,
      { field: 'date', operator: 'equals', value: fecha }
    ]);
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<Recordatorio[]> {
    return this.db.queryMultiple<Recordatorio>('recordatorios', [
      this.NOT_DELETED,
      {
        field: 'date',
        operator: 'between',
        value: [fechaInicio, fechaFin]
      }
    ], { column: 'date', direction: 'asc' });
  }

  async getByPrioridad(priority: 'low' | 'medium' | 'high'): Promise<Recordatorio[]> {
    return this.db.queryMultiple<Recordatorio>('recordatorios', [
      this.NOT_DELETED,
      { field: 'priority', operator: 'equals', value: priority }
    ]);
  }

  async getByUsuario(userId: string): Promise<Recordatorio[]> {
    return this.db.queryMultiple<Recordatorio>('recordatorios', [
      this.NOT_DELETED,
      { field: 'user_id', operator: 'equals', value: userId }
    ]);
  }

  async getByEmpresa(empresaId: string): Promise<Recordatorio[]> {
    return this.db.queryMultiple<Recordatorio>('recordatorios', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
  }

  async getPublicosByEmpresa(empresaId: string): Promise<Recordatorio[]> {
    return this.db.queryMultiple<Recordatorio>('recordatorios', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId },
      { field: 'is_public', operator: 'equals', value: true }
    ]);
  }

  async getPendientes(userId: string): Promise<Recordatorio[]> {
    return this.db.queryMultiple<Recordatorio>('recordatorios', [
      this.NOT_DELETED,
      { field: 'user_id', operator: 'equals', value: userId },
      { field: 'completed', operator: 'equals', value: false }
    ]);
  }

  async markAsCompleted(id: string): Promise<void> {
    await this.db.update('recordatorios', id, { completed: true });
    this.emitSync('UPDATE', { id, completed: true } as any);
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================

  setProfileRepository(profileRepo: IProfileRepository): void {
    this.profileRepo = profileRepo;
    super.setProfileRepository(profileRepo);
  }
}
